import { PlacementId } from './placementIds'
import { EmbroideryTypeKey, PlacedEmbroideryItem, RuleCheck, RuleId } from './types'

/**
 * Reusable rule functions. Each is a named business concept that takes its settings as parameters
 * and returns a configured check — so one function serves every flow that needs that shape of rule,
 * with its own numbers and its own reason code.
 *
 * A flow assembles its rulebook by calling these (see `flows/dtcEmbroidery.ts`). Nothing is
 * interpreted at runtime: by the time the engine runs, a rule is just a function.
 *
 * ## How to read one (they are all curried — two functions, called at two different times)
 *
 * ```
 * export const maxItemsAcross =
 *   (ruleId, placements, max): RuleCheck =>   // outer: the settings
 *   items =>                                  // inner: the check
 *     ...
 * ```
 *
 * The **outer** call happens once, when a flow builds its rulebook. It takes a `RuleId` naming the
 * rule plus whatever that rule needs to do its job (a type, a placement set, a number), and returns
 * the inner function with those values captured. Nothing is checked yet.
 *
 * The **inner** call happens every time the engine needs an answer. It takes the current selections
 * — the embroidery items the customer has selected, or defaults such as saved preferences — and returns a
 * `Violation[]`: empty if the rule is satisfied, otherwise one entry per problem, tagged with the
 * `ruleId` from the outer call so the caller knows which rule fired.
 *
 * So a flow file (`flows/dtcEmbroidery.ts`) is a list of outer calls, and `violations()` in
 * `engine.ts` runs every inner function against the selections and concatenates the
 * results. Note the engine calls it constantly, not just on submit: availability is derived by
 * probing "what if the customer added this here?", so assume the inner function runs once per
 * type × placement on every change.
 *
 * ## Adding a rule
 *
 * 1. Add its id to `RuleId` in `types.ts`.
 * 2. Reuse a function here if one fits — pass your own id and numbers. If none does, write a new one
 *    in the same shape: settings in, `RuleCheck` out.
 * 3. Call it from the flow's rulebook — the array in `flows/*.ts`. Where you put it in that array
 *    does not change what is legal: every rule runs, and a selection is legal only if all of them
 *    come back empty. It changes only which reason is reported *first* when several rules fail at
 *    once, because callers such as `canAddType` take the first violation and ignore the rest.
 *    So put broad garment-wide caps ahead of narrower placement rules: "you already have a logo" is
 *    more useful than "that spot is full" when both are true.
 */

// --- small helpers the rules read with ---------------------------------------------------------

const itemsOfType = (
  items: readonly PlacedEmbroideryItem[],
  typeKey: EmbroideryTypeKey
): PlacedEmbroideryItem[] => items.filter(item => item.typeKey === typeKey)

const itemsAt = (
  items: readonly PlacedEmbroideryItem[],
  placementId: PlacementId
): PlacedEmbroideryItem[] => items.filter(item => item.placementId === placementId)

const placementsUsed = (items: readonly PlacedEmbroideryItem[]): PlacementId[] => [
  ...new Set(items.map(item => item.placementId)),
]

const instanceIds = (items: readonly PlacedEmbroideryItem[]): string[] =>
  items.map(item => item.instanceId)

// --- the rules ---------------------------------------------------------------------------------

/** At most `max` items of one type on the garment. "No more than 3 text items total." */
export const maxItemsOfType =
  (ruleId: RuleId, typeKey: EmbroideryTypeKey, max: number): RuleCheck =>
  items => {
    const here = itemsOfType(items, typeKey)
    return here.length > max ? [{ reason: ruleId, typeKey, instanceIds: instanceIds(here) }] : []
  }

/** At most `max` items across a set of placements combined. "No more than 3 items on the chest." */
export const maxItemsAcross =
  (ruleId: RuleId, placements: readonly PlacementId[], max: number): RuleCheck =>
  items => {
    const here = items.filter(item => placements.includes(item.placementId))
    return here.length > max ? [{ reason: ruleId, instanceIds: instanceIds(here) }] : []
  }

/** At most `max` items on any single placement. "No more than 2 things on one spot." */
export const maxItemsPerPlacement =
  (ruleId: RuleId, max: number): RuleCheck =>
  items =>
    placementsUsed(items)
      .filter(placementId => itemsAt(items, placementId).length > max)
      .map(placementId => ({
        reason: ruleId,
        placementId,
        instanceIds: instanceIds(itemsAt(items, placementId)),
      }))

/**
 * At most `max` items of one type across a set of placements.
 * "At most 1 text item across both sleeves" — so one sleeve gets text, and it cannot be stacked.
 */
export const maxItemsOfTypeAcross =
  (
    ruleId: RuleId,
    typeKey: EmbroideryTypeKey,
    placements: readonly PlacementId[],
    max: number
  ): RuleCheck =>
  items => {
    const here = items.filter(
      item => item.typeKey === typeKey && placements.includes(item.placementId)
    )
    return here.length > max ? [{ reason: ruleId, typeKey, instanceIds: instanceIds(here) }] : []
  }

/**
 * At most `max` items drawn from a set of types on any single placement.
 * "One mark (icon or logo) per spot" — they may go anywhere, just never share a placement.
 */
export const maxItemsOfTypesPerPlacement =
  (ruleId: RuleId, typeKeys: readonly EmbroideryTypeKey[], max: number): RuleCheck =>
  items =>
    placementsUsed(items)
      .filter(
        placementId =>
          itemsAt(items, placementId).filter(item => typeKeys.includes(item.typeKey)).length > max
      )
      .map(placementId => ({
        reason: ruleId,
        placementId,
        instanceIds: instanceIds(
          itemsAt(items, placementId).filter(item => typeKeys.includes(item.typeKey))
        ),
      }))

/**
 * Two types may not occupy the same placement. "A logo may not sit on top of sleeve text."
 * Scope it with `placements` to limit the prohibition to part of the garment; omit for the whole.
 */
export const typesMayNotShareAPlacement =
  (
    ruleId: RuleId,
    typeA: EmbroideryTypeKey,
    typeB: EmbroideryTypeKey,
    placements?: readonly PlacementId[]
  ): RuleCheck =>
  items =>
    placementsUsed(items)
      .filter(placementId => !placements || placements.includes(placementId))
      .filter(placementId => {
        const here = itemsAt(items, placementId)
        return here.some(i => i.typeKey === typeA) && here.some(i => i.typeKey === typeB)
      })
      .map(placementId => ({
        reason: ruleId,
        placementId,
        instanceIds: instanceIds(itemsAt(items, placementId)),
      }))

/**
 * A type may ONLY go in these placements. e.g. "Icons go on sleeves, nowhere else."
 *
 * A restriction, not a count — the shape the other rules here cannot express. Note this overlaps
 * with anatomy (`PlacementRule.allowedTypes`), and the two are not interchangeable: anatomy is
 * per-GARMENT and shared by every flow, so it cannot say "the legacy flow offers icons on sleeves
 * only while the multi-placement flow offers them anywhere" about the same scrub top. A flow-scoped
 * placement policy has to be a rule.
 */
export const typeOnlyAllowedAt =
  (ruleId: RuleId, typeKey: EmbroideryTypeKey, placements: readonly PlacementId[]): RuleCheck =>
  items =>
    items
      .filter(item => item.typeKey === typeKey && !placements.includes(item.placementId))
      .map(item => ({
        reason: ruleId,
        typeKey,
        placementId: item.placementId,
        instanceIds: [item.instanceId],
      }))

/**
 * Two types may not both be on the garment at all — the pre-multi-placement logo-XOR-icon rule.
 */
export const typesMayNotCoexist =
  (ruleId: RuleId, typeA: EmbroideryTypeKey, typeB: EmbroideryTypeKey): RuleCheck =>
  items => {
    const a = itemsOfType(items, typeA)
    const b = itemsOfType(items, typeB)
    return a.length > 0 && b.length > 0
      ? [{ reason: ruleId, instanceIds: instanceIds([...a, ...b]) }]
      : []
  }
